import { useRef, useState, useEffect, useCallback } from 'react';

import SignatureCanvas from 'react-signature-canvas';

import { useSaveMySignMutation } from '@features/employee/hooks';
import Alert from '@shared/components/atoms/Alert';
import Button from '@shared/components/atoms/Button';
import Modal from '@shared/components/atoms/Modal/Modal';
import { useAlert } from '@shared/hooks';

import styles from './SignSettingModal.module.scss';

/**
 * 서명설정 모달 컴포넌트
 * - 서명 드로잉 또는 이미지 업로드로 서명 등록
 */
function SignSettingModal({ isOpen, onClose, onSuccess, signImageUrl }) {
  // TODO 서명 팝업의 크기 조절
  const signRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isImageMode, setIsImageMode] = useState(false);

  // Alert 상태 관리 (커스텀 훅 사용)
  const { alertState, showAlert, closeAlert } = useAlert();

  // 서명 저장 Mutation
  const saveMutation = useSaveMySignMutation({
    onSuccess: function () {
      onClose();
      showAlert('서명이 등록되었습니다.', onSuccess);
    },
    onError: function () {
      showAlert('서명 등록에 실패했습니다.');
    },
  });

  // 모달이 열릴 때 초기화 기존서명 존재시 기존서명을 넣음
  useEffect(() => {
    if (isOpen) {
      if (signImageUrl) {
        setUploadedImage(signImageUrl);
        setIsImageMode(true);
      } else {
        setUploadedImage(null);
        setIsImageMode(false);
        signRef.current?.clear();
      }
    }
  }, [isOpen, signImageUrl]);
  /**
   * 지우기 버튼 클릭 핸들러
   * - 서명 캔버스 초기화
   * - 업로드된 이미지 제거
   * - 파일 입력 필드 초기화
   */
  const handleClear = useCallback(function () {
    if (signRef.current) {
      signRef.current.clear();
    }
    setUploadedImage(null);
    setIsImageMode(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  /**
   * 파일 선택 핸들러
   * - 선택된 파일의 형식(jpg, png)과 크기(5MB 이하) 검증
   * - 유효한 파일인 경우 미리보기 이미지 생성 및 이미지 모드로 전환
   * @param {Event} e - 파일 입력 이벤트
   */
  const handleFileSelect = useCallback(
    function (e) {
      const file = e.target.files[0];
      if (!file) return;

      // 파일 형식 검증 (jpg, png만 허용)
      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showAlert('jpg, png 형식의 파일만 업로드 가능합니다.');
        e.target.value = '';
        return;
      }

      // 파일 크기 검증 (5MB 제한)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        showAlert('파일 크기는 5MB 이하로 제한됩니다.');
        e.target.value = '';
        return;
      }

      // 이미지 미리보기 생성
      const reader = new FileReader();
      reader.onload = function (event) {
        setUploadedImage(event.target.result);
        setIsImageMode(true);
        // 서명 캔버스 초기화
        if (signRef.current) {
          signRef.current.clear();
        }
      };
      reader.readAsDataURL(file);
    },
    [showAlert],
  );

  /**
   * 서명파일 선택 버튼 클릭 핸들러
   * - 숨겨진 파일 입력 필드를 프로그래밍 방식으로 클릭
   */
  const handleSelectFileClick = useCallback(function () {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  /**
   * 서명 등록 핸들러
   * - 이미지 모드: 업로드된 이미지를 Blob으로 변환하여 서버에 전송
   * - 드로잉 모드: 캔버스의 서명을 PNG Blob으로 변환하여 서버에 전송
   * - 서명이 없는 경우 경고 메시지 표시
   */
  const handleSave = useCallback(
    function () {
      // 이미지 업로드 모드인 경우
      if (isImageMode && uploadedImage) {
        // Base64 → Blob 변환
        fetch(uploadedImage)
          .then(function (res) {
            return res.blob();
          })
          .then(function (blob) {
            saveMutation.mutate(blob);
          })
          .catch(function () {
            showAlert('이미지 처리 중 오류가 발생했습니다.');
          });
        return;
      }

      // 서명 드로잉 모드인 경우
      if (!signRef.current || signRef.current.isEmpty()) {
        showAlert('서명을 먼저 작성해주세요.');
        return;
      }

      const canvas = signRef.current.getCanvas();
      canvas.toBlob(function (blob) {
        if (!blob) {
          showAlert('이미지 생성에 실패했습니다.');
          return;
        }
        saveMutation.mutate(blob);
      }, 'image/png');
    },
    [isImageMode, uploadedImage, saveMutation, showAlert],
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="서명 설정"
        size="small"
        closeOnOverlayClick={false}
        classes={{
          modalHeader: styles.customHeader,
          modalTitle: styles.customTitle,
        }}
      >
        <div className={styles.modalContent}>
          {/* 버튼 그룹 */}
          <div className={styles.buttonRow}>
            <div className={styles.rightButtons}>
              <Button variant="default" className={styles.actionButton} onClick={handleClear}>
                지우기
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className={styles.hiddenInput}
              />
              <Button
                variant="default"
                className={styles.actionButton}
                onClick={handleSelectFileClick}
              >
                서명파일 선택
              </Button>
            </div>
          </div>
          {/* 서명 드로잉 영역 */}
          <div className={styles.signCanvasWrapper}>
            {isImageMode && uploadedImage ? (
              <div className={styles.uploadedImageContainer}>
                <img src={uploadedImage} alt="업로드된 서명" className={styles.uploadedImage} />
              </div>
            ) : (
              <SignatureCanvas
                ref={signRef}
                penColor="black"
                canvasProps={{
                  className: styles.signCanvas,
                }}
              />
            )}
          </div>

          {/* 하단 버튼 그룹 */}
          <div className={styles.buttonGroup}>
            <Button variant="default" className={styles.submitButton} onClick={onClose}>
              닫기
            </Button>
            <Button
              variant="default"
              className={styles.submitButton}
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? '등록 중...' : '등록'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Alert 컴포넌트 */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </>
  );
}

export default SignSettingModal;
